import React, { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import type { AnalysisArSource } from "@/types/analysisRun";

import CollapsibleSection from "./CollapsibleSection";

const CONSISTENCY_STYLES: Record<AnalysisArSource["consistency"], string> = {
  consistent: "bg-emerald-100 text-emerald-800",
  variable: "bg-amber-100 text-amber-800",
  declining: "bg-red-100 text-red-800",
  sporadic: "bg-orange-100 text-orange-800",
  unknown: "bg-muted text-muted-foreground",
};

function fmtMoney(value: number | null): string {
  if (value == null) return "—";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Null % sorts last so known concentrations stay on top. */
function byPctOfRevenueDesc(a: AnalysisArSource, b: AnalysisArSource): number {
  return (
    (b.pct_of_revenue ?? Number.NEGATIVE_INFINITY) - (a.pct_of_revenue ?? Number.NEGATIVE_INFINITY)
  );
}

interface Props {
  sources: AnalysisArSource[];
}

/**
 * Receivables-quality section: revenue grouped by payer with concentration,
 * cadence, and reliability commentary. Totals/percentages are computed
 * deterministically on the backend; qualitative fields come from the model.
 */
const ArAnalysisSection: React.FC<Props> = ({ sources }) => {
  const ordered = useMemo(() => [...sources].sort(byPctOfRevenueDesc), [sources]);

  if (sources.length === 0) {
    return (
      <CollapsibleSection title="AR Analysis" defaultOpen>
        <p className="text-sm text-muted-foreground">No revenue sources identified.</p>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection title="AR Analysis (revenue sources)" defaultOpen>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              <th className="py-2 pr-4">Source</th>
              <th className="py-2 pr-4 text-right">Total</th>
              <th className="py-2 pr-4 text-right">% of revenue</th>
              <th className="py-2 pr-4">Cadence</th>
              <th className="py-2 pr-4">Consistency</th>
              <th className="py-2 pr-4">Last seen</th>
              <th className="py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((s) => (
              <tr key={s.name} className="border-b align-top last:border-0">
                <td className="py-2 pr-4 font-medium">{s.name}</td>
                <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(s.total)}</td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {s.pct_of_revenue != null ? `${s.pct_of_revenue.toFixed(1)}%` : "—"}
                </td>
                <td className="py-2 pr-4">{s.cadence || "—"}</td>
                <td className="py-2 pr-4">
                  <Badge variant="outline" className={CONSISTENCY_STYLES[s.consistency]}>
                    {s.consistency}
                  </Badge>
                </td>
                <td className="whitespace-nowrap py-2 pr-4">{s.last_seen_date || "—"}</td>
                <td className="py-2 text-muted-foreground">{s.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
};

export default ArAnalysisSection;
